import { createMetaManager } from "vue-meta";
import { useMeta } from "vue-meta";
import { useI18n } from "vue-i18n";
import { computed, ref, watchEffect } from "vue";
import _ from "lodash/object";
import { hasProperty } from "@/client/utilities";

export default {
  install(app) {
    let manager = createMetaManager({ isSSR: utilities.isSSR() });

    app.use(manager);
    app.config.globalProperties.$metaManager = manager;
    app.mixin({
      setup(a, b, c) {},
      updated() {},
      created() {
        let generateUseMeta = (args) => {
          let extractMetaObjectFromArgs = () => {
            if (typeof args === "string") {
              args = { title: args };
            }

            if (!args || typeof args !== "object") {
              args = {};
            }

            let translatedArgs = {};
            for (const [key, value] of Object.entries(args)) {
              if (typeof value === "string") {
                try {
                  let translated = this.t(args[key]);
                  if (typeof translated === "string" && translated !== "") {
                    translatedArgs[key] = this.t(args[key]);
                  }
                } catch (e) {}
              } else {
                translatedArgs[key] = args[key];
              }
            }
            return {
              ...translatedArgs,
            };
          };

          let metaArgs = extractMetaObjectFromArgs();
          if (
            !typeof metaArgs.title === "string" &&
            (typeof meta.title !== "string" || meta.title !== "")
          ) {
            // meta.title = "-";
          }
          let meta = useMeta(metaArgs).meta;

          // watch locale and change meta - it is not properly reactive on it's own
          watchEffect(() => {
            let newMetaArgs = extractMetaObjectFromArgs();
            // title and dsecription are required to be strings.
            if (typeof newMetaArgs.title !== "string") {
              newMetaArgs.title = "";
            }
            if (typeof newMetaArgs.description !== "string") {
              newMetaArgs.description = "";
            }
            for (const [key, value] of Object.entries(newMetaArgs)) {
              meta[key] = value;
            }
          });

          return meta;
        };

        // apply meta tags automatcally, if assigned
        let accessor = this[config.metaDataAccessorKey];

        if (!accessor) {
          return;
        }

        let targetMeta = accessor();
        let i18n = useI18n();

        // case string - is page title
        if (typeof targetMeta === "string") {
          this.meta = generateUseMeta({ title: targetMeta });
          return this.meta;
        }

        if (typeof targetMeta === "function") {
          let finalMeta = targetMeta({ instance: this, useMeta, i18n });
          this.meta = useMeta(finalMeta).meta;
          return this.meta;
        }

        // case not object - we cant handle
        if (typeof targetMeta !== "object") {
          this.meta = useMeta({}).meta;
          return this.meta;
        }

        // meta is object - create an instance, who is watched, for translations on locale change
        let finalMetaConf = { ...targetMeta };
        this.meta = generateUseMeta(finalMetaConf);
        return this.meta;
      },
      mounted() {},
      methods: {
        setRobotsMetaTag(value = null, meta = null) {
          let target;

          try {
            target = meta || this.meta;
          } catch (e) {
            warn("setRobotsMetaTag, can not get meta manager instance", {
              value,
              component: this,
            });
            return;
          }

          if (!target) {
            warn("setRobotsMetaTag, can not get meta manager instance", {
              value,
              component: this,
            });
            return;
          }

          if (!value || value === "") {
            value = config.seo.defaultRobotsMetaTagValue;
          }

          target["robots"] = {
            tag: "meta",
            name: "robots",
            content: value,
          };
        },
        setMetaByMetaObject(metaObject, meta) {
          let target;

          try {
            target = meta || this.meta;
          } catch (e) {
            warn("setMetaByState, can not get meta manager instance", {
              metaObject,
              meta,
              component: this,
            });
            return;
          }

          if (!target) {
            warn("setMetaByState, can not get meta manager instance", {
              metaObject,
              meta,
              component: this,
            });
            return;
          }

          target.title = metaObject?.title;

          target.description = metaObject?.description;
          target["og:title"] = {
            tag: "meta",
            name: "og:title",
            content: utilities.isNonEmptyString(metaObject?.metaTitle)
              ? metaObject?.metaTitle
              : metaObject?.title,
          };
          target["og:description"] = {
            tag: "meta",
            name: "og:description",
            property: "og:description",
            content: metaObject?.description,
          };

          this.setRobotsMetaTag(metaObject.robots);
          if (
            metaObject?.ogImage &&
            metaObject?.ogImage !== "" &&
            metaObject?.ogImage.length > 3
          ) {
            target["og:image"] = {
              tag: "meta",
              name: "og:image",
              property: "og:image",
              content: metaObject?.ogImage,
            };
            target["og:image:secure_url"] = {
              tag: "meta",
              name: "og:image:secure_url",
              property: "og:image:secure_url",
              content: metaObject?.ogImage,
            };

            // temp test for whatsapp og image
            target["og:image:height"] = {
              tag: "meta",
              name: "og:image:height",
              property: "og:image:height",
              content: "200",
            };
            target["og:image:width"] = {
              tag: "meta",
              name: "og:image:width",
              property: "og:image:width",
              content: "200",
            };
            target["og:image:type"] = {
              tag: "meta",
              name: "og:image:type",
              property: "og:image:type",
              content: "image/png",
            };
          }
        },
        updateMetaByState() {
          // just make sure we have a metadata accessor. This also makes it so that
          // non-page components will not set meta by default
          if (!this[config.metaDataAccessorKey]) {
            return;
          }

          let isMetaObject = (item) => {
            let metaFields = ["title", "metaTitle", "description", "ogImage"];

            if (!utilities.isObject(item)) {
              return false;
            }

            metaFields.forEach((key) => {
              if (!utilities.hasProperty(item, key)) {
                return false;
              }
            });

            return true;
          };

          let metaDefinition = this[config.metaDataAccessorKey]();

          if (utilities.hasProperty(metaDefinition, "metaObjectPath")) {
            // we are told where the meta is
            let candidate = _.get(this, metaDefinition.metaObjectPath, false);

            if (isMetaObject(candidate)) {
              this.setMetaByMetaObject(candidate);
            } else {
              warn(
                "page metaObjectPath provided in meta, but no Meta Object found there!"
              );
            }

            // do not continue - we were told where to look. we will not look elsewhere.
            return;
          }

          // now specific path for meta provided. lets look in the first async item as a default behaviour
          // no async data. nothing we can do
          if (!utilities.isObject(this.asyncData)) {
            return;
          }

          let firstKey = Object.keys(this.asyncData)[0];

          // if we have a valid meta object under the first async data key - we can set it
          if (
            typeof firstKey === "string" &&
            utilities.hasProperty(this[firstKey], "meta") &&
            isMetaObject(this[firstKey].meta)
          ) {
            this.setMetaByMetaObject(this[firstKey].meta);
          } else {
            this.setRobotsMetaTag();
          }
        },
      },
    });
  },
};
